function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);

    if (enumerableOnly) {
      symbols = symbols.filter(function (sym) {
        return Object.getOwnPropertyDescriptor(object, sym).enumerable;
      });
    }

    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }

  return arr2;
}

function _iterableToArrayLimit(arr, i) {
  var _i = arr && (typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]);

  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;

  var _s, _e;

  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

import React, { useState, useMemo, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { nanoid } from "nanoid";
import { useSelect } from "downshift";
import useField from "../hooks/internal/useField";
import useWindow from "../hooks/useWindow";
import { responsiveMaxHeightType } from "../hooks/useResponsiveProp";
import useAllResponsiveProps from "../hooks/useAllResponsiveProps";
import { mergeProps, areDropdownOptionsValid } from "../utils/component";
import Field from "./internal/Field";
import InternalDropdown from "./internal/InternalDropdown";
import { jsx as ___EmotionJSX } from "@emotion/core";
var COLORS = InternalDropdown.COLORS;

function isOptionSelected(options, value) {
  return options.findIndex(function (option) {
    return option.value === value;
  }) > -1;
}

var DEFAULT_PROPS = {
  hideLabel: false,
  color: InternalDropdown.DEFAULT_PROPS.color,
  placeholderComponent: InternalDropdown.DEFAULT_PROPS.placeholderComponent,
  disabled: false,
  validate: function validate(value, _ref) {
    var isEmpty = _ref.isEmpty;

    if (isEmpty(value)) {
      return "Please make a selection.";
    }

    return null;
  }
};
Dropdown.COLORS = COLORS;
Dropdown.DEFAULT_PROPS = DEFAULT_PROPS;

function Dropdown(props) {
  var mergedProps = mergeProps(props, DEFAULT_PROPS, {}, {
    hideLabel: function hideLabel(_hideLabel) {
      return typeof _hideLabel === "boolean";
    },
    color: function color(_color) {
      return COLORS.includes(_color);
    },
    placeholderComponent: function placeholderComponent(_placeholderComponent) {
      return typeof _placeholderComponent === "function";
    },
    optionToString: function optionToString(_optionToString) {
      return typeof _optionToString === "function";
    },
    optionComponent: function optionComponent(_optionComponent) {
      return typeof _optionComponent === "function";
    },
    helpText: function helpText(_helpText) {
      return typeof _helpText === "string";
    },
    disabled: function disabled(_disabled) {
      return typeof _disabled === "boolean";
    },
    options: function options(_options) {
      return areDropdownOptionsValid(_options);
    },
    onChange: function onChange(_onChange) {
      return typeof _onChange === "function";
    }
  });
  var name = mergedProps.name,
      label = mergedProps.label,
      hideLabel = mergedProps.hideLabel,
      placeholderComponent = mergedProps.placeholderComponent,
      optionToString = mergedProps.optionToString,
      optionComponent = mergedProps.optionComponent,
      options = mergedProps.options,
      helpText = mergedProps.helpText,
      disabled = mergedProps.disabled,
      validate = mergedProps.validate,
      validateData = mergedProps.validateData,
      propsOnChange = mergedProps.onChange,
      testId = mergedProps.testId,
      __internal__focus = mergedProps.__internal__focus,
      __internal__open = mergedProps.__internal__open,
      __internal__highlightedIndex = mergedProps.__internal__highlightedIndex;

  if (!options) {
    throw new Error("Dropdown options should have the following format: [{ data: { ... }, value: \"option-value\" }, ...]");
  }

  var _useState = useState(function () {
    return "dropdown-aux-".concat(nanoid());
  }),
      _useState2 = _slicedToArray(_useState, 1),
      auxId = _useState2[0];

  var isEmpty = useCallback(function (value) {
    return isOptionSelected(options, value) === false;
  }, [options]);
  var data = useMemo(function () {
    return _objectSpread({
      isEmpty: isEmpty
    }, validateData && {
      data: validateData
    });
  }, [isEmpty, validateData]);

  var _useField = useField("Dropdown", {
    name: name,
    disabled: disabled,
    optional: false,
    validate: validate,
    data: data
  }),
      value = _useField.value,
      errors = _useField.errors,
      hasErrors = _useField.hasErrors,
      onFocus = _useField.onFocus,
      onBlur = _useField.onBlur,
      fieldOnChange = _useField.onChange;

  var windowFromContext = useWindow();
  var buttonRef = useRef();
  var itemToString = useCallback(function (option) {
    return option ? optionToString(option) : "";
  }, [optionToString]);
  var initialSelectedItem = options.find(function (option) {
    return option.value === value;
  });

  var _useSelect = useSelect({
    items: options,
    itemToString: itemToString,
    initialSelectedItem: initialSelectedItem,
    onSelectedItemChange: function onSelectedItemChange(_ref2) {
      var selectedOption = _ref2.selectedItem;
      fieldOnChange({
        target: buttonRef.current,
        value: selectedOption.value
      });
      propsOnChange && propsOnChange({
        selectedOption: selectedOption
      });
    },
    environment: windowFromContext
  }),
      isOpen = _useSelect.isOpen,
      selectedOption = _useSelect.selectedItem,
      highlightedIndex = _useSelect.highlightedIndex,
      getToggleButtonProps = _useSelect.getToggleButtonProps,
      getMenuProps = _useSelect.getMenuProps,
      getItemProps = _useSelect.getItemProps;

  var isValid = !hasErrors;
  var describedBy = helpText || hasErrors ? auxId : null;
  var toggleButtonProps = useMemo(function () {
    return getToggleButtonProps({
      onFocus: onFocus,
      onBlur: onBlur,
      disabled: disabled,
      "aria-invalid": isValid ? null : "true",
      "aria-describedby": describedBy,
      ref: buttonRef
    });
  }, [getToggleButtonProps, onFocus, onBlur, disabled, isValid, describedBy]);
  var maxHeightProps = useAllResponsiveProps(props, "maxHeight");
  return ___EmotionJSX(Field, {
    optional: false,
    disabled: disabled,
    label: label,
    hideLabel: hideLabel,
    labelFor: toggleButtonProps.id,
    auxId: auxId,
    helpText: helpText,
    errors: errors,
    testId: testId
  }, ___EmotionJSX(InternalDropdown, _extends({
    name: name,
    color: props.color,
    placeholderComponent: placeholderComponent,
    optionComponent: optionComponent,
    options: options,
    selectedOption: selectedOption,
    isOpen: isOpen,
    toggleButtonProps: toggleButtonProps,
    getMenuProps: getMenuProps,
    getItemProps: getItemProps,
    highlightedIndex: highlightedIndex,
    onFocus: onFocus
  }, maxHeightProps, {
    __internal__focus: __internal__focus,
    __internal__open: __internal__open,
    __internal__highlightedIndex: __internal__highlightedIndex
  })));
}

Dropdown.propTypes = _objectSpread(_objectSpread({
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  hideLabel: PropTypes.bool,
  color: PropTypes.oneOf(COLORS),
  placeholderComponent: PropTypes.func,
  optionToString: PropTypes.func.isRequired,
  optionComponent: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({
    data: PropTypes.object.isRequired,
    value: PropTypes.string.isRequired
  })).isRequired,
  helpText: PropTypes.string,
  disabled: PropTypes.bool
}, responsiveMaxHeightType), {}, {
  validate: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
  validateData: PropTypes.any,
  onChange: PropTypes.func,
  testId: PropTypes.string,
  __internal__focus: PropTypes.bool,
  __internal__open: PropTypes.bool,
  __internal__highlightedIndex: PropTypes.number
});
export default Dropdown;